<template>
  <v-list-item
    :key="account.id"
    :to="linkTo"
    :class="rowColor"
    three-line
    density="compact"
  >
    <template #prepend>
      <v-avatar
        size="40"
        class="ma-1"
        :style="getBorderStyle()"
        @click.prevent="editImage"
      >
        <img
          v-if="account.icon != ''"
          :src="account.icon"
          :alt="account.name"
        />
        <avatar
          v-else
          :name="account.short || 'X'"
          :size="40"
          :rounded="true"
        />
      </v-avatar>
    </template>

    <template #default>
      <div class="py-0">
        <v-list-item-title class="text-subtitle-1">
          <span v-html="$sanitizeHtml(account.name)" />
          <v-icon
            v-if="account.gender == 'm'"
            small
            style="margin-left: 5px; margin-top: -2px"
            >mdi-gender-male</v-icon
          >
          <v-icon v-else small style="margin-left: 5px; margin-top: -2px"
            >mdi-gender-female</v-icon
          >
          <CountryFlag
            v-if="account.country"
            :country="account.country"
            size="small"
            style="position: relative; left: 3px; top: 3px"
          />
          <v-icon
            v-if="account.repat"
            color="green"
            small
            style="margin-left: 5px; margin-top: -2px"
            >mdi-star-circle</v-icon
          >
          <v-icon
            v-if="pastDeadlinesCount > 0"
            color="red"
            small
            style="margin-left: 5px; margin-top: -2px"
            >mdi-alert</v-icon
          >
        </v-list-item-title>

        <v-list-item-subtitle v-if="account.last_seen" class="text-caption">
          Laatst gezien: {{ formatTimeFromNow(account.last_seen) }}
        </v-list-item-subtitle>

        <v-list-item-subtitle class="py-0">
          <!-- Distance chip (shown first when in geolocation mode) -->
          <v-chip
            v-if="account.formatted_distance"
            class="ma-0 mr-1"
            size="small"
            density="comfortable"
            prepend-icon="mdi-map-marker-distance"
          >
            {{ account.formatted_distance }}
          </v-chip>

          <!-- Location chip -->
          <v-chip
            v-if="account.primary_location_display"
            class="ma-0"
            size="small"
            density="comfortable"
            prepend-icon="mdi-map-marker"
          >
            {{ account.primary_location_display }}
          </v-chip>

          <!-- Birth date chip -->
          <v-chip
            v-if="account.date_of_birth"
            color="orange-darken-2"
            variant="elevated"
            class="ml-1"
            size="small"
            density="comfortable"
          >
            <template #prepend>
              <v-icon size="small">mdi-calendar</v-icon>
            </template>
            {{ formatCalendarDate(account.date_of_birth) }}
          </v-chip>
        </v-list-item-subtitle>
      </div>
    </template>

    <template v-if="detail" #append>
      <div class="d-flex">
        <v-btn
          v-if="account.can_destroy"
          outlined
          fab
          dark
          x-small
          color="warning"
          class="mr-1"
          @click="modal = !modal"
        >
          <v-icon size="small">mdi-account-remove</v-icon>
        </v-btn>
        <ConfirmationDialog
          v-model:model-value="modal"
          title="Account verwijderen"
          message="Weet je zeker dat je dit account wilt verwijderen? There's no way back!!"
          @cancel="modal = !modal"
          @confirm="deleteAccount"
        />
        <v-btn
          v-if="account.can_edit"
          outlined
          fab
          dark
          x-small
          color="primary"
          @click="editAccount"
        >
          <v-icon size="small">mdi-account-edit</v-icon>
        </v-btn>
      </div>
    </template>
  </v-list-item>
</template>

<script>
import CountryFlag from "vue-country-flag-next";
import ConfirmationDialog from "../ui/ConfirmationDialog.vue";
import moment from "moment";
import Avatar from "vue3-avatar";
import { useDeadlinesStoreRefactor } from "@/stores/deadlinesStoreRefactor";
import { useFormsStore } from "@/stores/formsStore";
import { useAccountsStoreRefactor } from "@/stores/accountsStoreRefactor";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";

export default {
  name: "AccountListItem",
  components: { CountryFlag, ConfirmationDialog, Avatar },
  props: ["account", "detail", "hideMissingData"],
  setup(props) {
    const deadlinesStore = useDeadlinesStoreRefactor();
    const formsStore = useFormsStore();
    const accountsStore = useAccountsStoreRefactor();
    const router = useRouter();
    const modal = ref(false);

    const pastDeadlinesCount = computed(() => {
      const now = moment();
      return deadlinesStore.deadlines.filter(
        (deadline) =>
          deadline.account_id === props.account.id &&
          moment(deadline.deadline).isBefore(now),
      ).length;
    });

    const linkTo = computed(() => {
      if (props.detail) return "";
      return `/accounts/${props.account.id}`;
    });

    const rowColor = computed(() => {
      if (props.account.missing_fields && !props.hideMissingData) {
        return "bg-red-lighten-5 mb-1 rounded border-s-4 border-red-lighten-3";
      }
      return ""; // Return empty string if no specific class needed
    });

    const formatTimeFromNow = (date) => {
      return moment(date).fromNow();
    };

    const formatCalendarDate = (date) => {
      return moment(date).format("DD/MM/YYYY");
    };

    const getBorderStyle = () => {
      if (props.account.vip) {
        return "border:3px solid #ffbf00; box-shadow: 0 0 10px 5px #ffbf00;";
      }
      return ""; // Return empty string when no border style is needed
    };

    const editImage = () => {
      console.log("Editing account image, profile data:", {
        id: props.account.profile_image_as_id,
        icon_id: props.account.icon_id,
        icon: props.account.icon,
        icon_lg: props.account.icon_lg,
        profile_image_as_file: props.account.profile_image_as_file,
      });

      formsStore.edit({
        type: "image",
        item: {
          id: props.account.icon_id || null,
          can_change_image: true,
          // Use the larger image (icon_lg) for the pop-up display, fallback to profile_image_as_file or icon
          file:
            props.account.icon_lg ||
            props.account.profile_image_as_file ||
            props.account.icon ||
            null,
          file_dl:
            props.account.profile_image_as_file_dl ||
            props.account.icon_dl ||
            null,
          recordId: props.account.id,
        },
        options: {
          filetype: "image/png, image/jpeg",
          recordType: "Account",
          field: "profile_image_as",
          // Removed onComplete callback - ActionCable will handle the update
        },
      });
    };

    const editAccount = () => {
      formsStore.edit({
        type: "account",
        item: JSON.parse(JSON.stringify(props.account)), // Create a deep copy to avoid reactivity issues
      });
    };

    const deleteAccount = async () => {
      await accountsStore.destroy(props.account);
      router.replace("/accounts");
      modal.value = false;
    };

    return {
      modal,
      pastDeadlinesCount,
      linkTo,
      rowColor,
      formatTimeFromNow,
      formatCalendarDate,
      getBorderStyle,
      editImage,
      editAccount,
      deleteAccount,
    };
  },
};
</script>
